import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Modal } from './patterns';
import { PhotoData } from '../types/scouting';
import { formatDateTime } from '../dateFormat';

interface PhotoEntry extends PhotoData {
  scouterName?: string;
}

interface PhotoViewModalProps {
  show: boolean;
  onHide: () => void;
  photos: PhotoEntry[];
  scouterName?: string;
}

const PhotoViewModal: React.FC<PhotoViewModalProps> = ({ show, onHide, photos, scouterName }) => {
  const { t } = useTranslation();
  const [enlargedIndex, setEnlargedIndex] = useState<number | null>(null);

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} title={t('photoViewModal.title')} size="lg">
      <Modal.Body>
        {photos.length === 0 ? (
          <p className="text-center text-muted my-4">{t('photoViewModal.noPhotos')}</p>
        ) : enlargedIndex !== null ? (
          <div className="text-center">
            <img
              src={photos[enlargedIndex].photoUrl}
              alt="enlarged"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '0.5rem' }}
            />
            <div className="mt-2 text-muted small">
              {t('photoViewModal.tree')} {photos[enlargedIndex].treeIndex} &bull; {t('photoViewModal.point')} {photos[enlargedIndex].pointIndex} &bull; {formatDateTime(photos[enlargedIndex].timestamp)}
              {photos[enlargedIndex].pestId && ` \u2022 ${photos[enlargedIndex].pestId}`}
              {(photos[enlargedIndex].scouterName || scouterName) && ` \u2022 ${photos[enlargedIndex].scouterName || scouterName}`}
            </div>
            <Button variant="outline-secondary" size="sm" className="mt-2" onClick={() => setEnlargedIndex(null)}>
              {t('photoViewModal.backToGrid')}
            </Button>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '1rem'
            }}
          >
            {photos.map((photo, idx) => (
              <div
                key={idx}
                style={{ cursor: 'pointer', borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #e5e7eb' }}
                onClick={() => setEnlargedIndex(idx)}
              >
                <img
                  src={photo.photoUrl}
                  alt={`${t('photoViewModal.tree')} ${photo.treeIndex} ${t('photoViewModal.point')} ${photo.pointIndex}`}
                  style={{ width: '100%', height: '100px', objectFit: 'cover', display: 'block' }}
                />
                <div className="p-1" style={{ fontSize: '0.7rem', color: '#374151', background: '#f9fafb' }}>
                  <div><strong>{t('photoViewModal.tree')} {photo.treeIndex}</strong> &bull; {t('photoViewModal.point')} {photo.pointIndex}</div>
                  <div className="text-muted">{formatDateTime(photo.timestamp)}</div>
                  {photo.pestId && <div>{photo.pestId}</div>}
                  <div className="text-muted">{photo.scouterName || scouterName || ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="pat-btn pat-btn--subtle" onClick={onHide}>
          {t('common.close')}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default PhotoViewModal;
